import { useNavigate, useLocation } from "react-router-dom";
import { MapPin, Compass, Library, CreditCard, User, LogIn } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import UserAvatar from "./UserAvatar";
import { Button } from "./ui/button";

const NAV_ITEMS = [
  { path: "/explore",    icon: MapPin,          label: "Explore" },
  { path: "/plan-tour",  icon: Compass,         label: "Plan a Trip" },
  { path: "/dashboard",  icon: Library,          label: "Library" },
  { path: "/pricing",    icon: CreditCard,       label: "Buy Credits", isCredits: true },
  { path: "/settings",   icon: User,             label: "Profile" },
];

export const DesktopSidebar = ({ active }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const currentPath = active || location.pathname;

  const handleNavClick = (item) => {
    if (item.path === "/plan-tour" && currentPath === "/plan-tour") {
      // If already on Plan a Trip, reset to step 1
      sessionStorage.removeItem("cv_tour_last");
      window.location.href = "/plan-tour";
    } else {
      navigate(item.path);
    }
  };

  return (
    <aside
      className="hidden lg:flex flex-col fixed left-0 top-0 h-screen w-64 bg-card border-r border-border z-20"
      data-testid="desktop-sidebar"
    >
      {/* Logo — clickable to homepage */}
      <div className="p-6 border-b border-border">
        <button
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => navigate("/")}
          data-testid="desktop-sidebar-home-link"
        >
          <img src="/icon-192.png" alt="Culture Ken" className="w-10 h-10 rounded-xl" />
          <span className="text-xl font-bold text-foreground">Culture Ken</span>
        </button>
      </div>

      {/* Nav items */}
      <nav className="flex-1 p-4 space-y-2">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = currentPath === item.path;
          return (
            <Button
              key={item.path}
              variant="ghost"
              className={`w-full justify-start h-12 text-base ${
                isActive ? "bg-muted text-foreground font-semibold" : ""
              }`}
              onClick={() => handleNavClick(item)}
              data-testid={`sidebar-${item.path.replace("/", "")}`}
            >
              <Icon className="w-5 h-5 mr-3" />
              {item.label}
              {item.isCredits && (
                <span className="ml-auto text-xs tabular-nums px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground">
                  {user?.credits || 0}
                </span>
              )}
            </Button>
          );
        })}
      </nav>

      {/* User info at bottom */}
      <div className="p-4 border-t border-border">
        {user ? (
          <div className="flex items-center gap-3" data-testid="desktop-sidebar-user-info">
            <UserAvatar user={user} size="sm" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-foreground truncate">{user.name || user.email?.split("@")[0]}</p>
              <p className="text-sm text-muted-foreground">{user.credits || 0} credits</p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <Button
              className="w-full h-12 rounded-xl"
              onClick={() => navigate("/login")}
              data-testid="sidebar-login-btn"
            >
              <LogIn className="w-5 h-5 mr-2" />
              Sign In
            </Button>
            <Button
              variant="outline"
              className="w-full h-10 rounded-xl text-sm"
              onClick={() => navigate("/signup")}
              data-testid="sidebar-signup-btn"
            >
              Create Account
            </Button>
          </div>
        )}
      </div>
    </aside>
  );
};
